import { createSlice } from "@reduxjs/toolkit";
import uniqBy from "lodash.uniqby";

import { RequestStatus } from "@fitness-app/data-models";

import { fetchNutritionTrackedWeek } from "./actions/fetchNutritionTrackedWeek";
import { upsertNutritionTrackedDay } from "./actions/upsertNutritionTrackedDay";
import { getNutritionEventWeek } from "./helpers/getNutritionEventWeek";
import { TRAINEE_NUTRITION_TRACKER_REDUCER_NAME, type TraineeNutritionTrackerReducer } from "./types";

const initialState: TraineeNutritionTrackerReducer = {
  selectedWeek: null,
  listStatus: null,
  fetchedWeekDays: [],
  fetchedWeek: null,
};

const reducerSlice = createSlice({
  initialState,
  name: TRAINEE_NUTRITION_TRACKER_REDUCER_NAME,
  reducers: {
    resetFetchedWeek: (state) => {
      state.fetchedWeekDays = [];
      state.listStatus = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchNutritionTrackedWeek.pending, (state, { meta }) => {
      // Track the latest requested week so out-of-order responses can be discarded below.
      const requestedWeek = getNutritionEventWeek(meta.arg.date);
      state.selectedWeek = requestedWeek;
      if (!state.fetchedWeek || state.fetchedWeek !== requestedWeek) {
        state.listStatus = RequestStatus.FETCHING;
      }
    });
    builder.addCase(fetchNutritionTrackedWeek.fulfilled, (state, { payload, meta }) => {
      const week = getNutritionEventWeek(meta.arg.date);
      // Discard stale responses: when weeks are switched quickly, an earlier week's request
      // can resolve after the current one. Without this guard it would clobber fetchedWeekDays
      // with the wrong week and the selected day would fall back to the meals-plan template.
      if (state.selectedWeek && week !== state.selectedWeek) {
        return;
      }
      state.listStatus = RequestStatus.SUCCESS;
      state.fetchedWeekDays = payload;
      state.fetchedWeek = week;
    });
    builder.addCase(fetchNutritionTrackedWeek.rejected, (state, { meta }) => {
      const week = getNutritionEventWeek(meta.arg.date);
      if (state.selectedWeek && week !== state.selectedWeek) {
        return;
      }
      // FAILED, not FETCHING: leaving the status on FETCHING after a network blip parked
      // every consumer gating on it (the tracker screen's skeleton) in a permanent
      // loading state until some later action happened to overwrite it (TRE-1889).
      state.listStatus = RequestStatus.FAILED;
    });
    builder.addCase(upsertNutritionTrackedDay.pending, (state, { meta }) => {
      state.fetchedWeekDays = uniqBy([meta.arg.nutritionDay, ...state.fetchedWeekDays], "eventDate");
    });
    builder.addCase(upsertNutritionTrackedDay.fulfilled, (state, { payload }) => {
      state.fetchedWeekDays = uniqBy([payload, ...state.fetchedWeekDays], "eventDate");
    });
  },
});

export const { resetFetchedWeek } = reducerSlice.actions;
export default reducerSlice.reducer;
